import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Flame, BookOpen, MapPin, MessagesSquare, ArrowRight, Droplets, Gauge, Waves, GraduationCap } from "lucide-react";
import { Button } from "../components/ui/button";
import AdSlot from "../components/AdSlot";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1721244654392-9c912a6eb236";
const COMMUNITY = "https://images.unsplash.com/photo-1569496736555-47c448d556f7?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";

const standards = [
  { code: "NFPA 13", title: "Sprinkler Systems", desc: "Design & installation of automatic sprinkler systems — spacing, densities, hydraulics.", icon: Droplets },
  { code: "NFPA 14", title: "Standpipe & Hose", desc: "Standpipe and hose systems — classes, pressures, and connection requirements.", icon: Waves },
  { code: "NFPA 20", title: "Fire Pumps", desc: "Stationary pumps for fire protection — capacities, drivers, and controllers.", icon: Gauge },
];

const features = [
  { icon: BookOpen, title: "Code Knowledge Base", desc: "Searchable, editable reference on NFPA 13, 14 & 20.", to: "/knowledge" },
  { icon: GraduationCap, title: "Design & Installation Training", desc: "Four progressive courses plus a field mistakes guide.", to: "/training" },
  { icon: MapPin, title: "Live Contractor Map", desc: "Find and pin contractors, engineers & suppliers.", to: "/map" },
  { icon: MessagesSquare, title: "Community Forum", desc: "Ask questions and share field knowledge.", to: "/forum" },
];

export default function Home() {
  const { openAuth } = useOutletContext();
  const { user } = useAuth();
  const { data: threads = [] } = useQuery({ queryKey: ["threads-home"], queryFn: async () => (await api.get("/forum/threads")).data });

  return (
    <div>
      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-[0.08]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-12 gap-8 py-20 md:py-28">
          <div className="md:col-span-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 border border-foreground px-3 py-1 mb-6">
                <Flame className="w-4 h-4 text-primary" />
                <span className="label-tech">The Fire Sprinkler Network</span>
              </div>
              <h1 className="font-display text-5xl lg:text-7xl tracking-tighter font-black leading-[0.95]">
                Where the fire sprinkler industry <span className="text-primary">connects</span>.
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
                A dynamic map, a living NFPA 13 / 14 / 20 knowledge base, and an active forum —
                built for contractors, engineers, and suppliers.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {!user && (
                  <Button onClick={openAuth} className="rounded-none bg-primary hover:bg-[#D62828] font-bold h-12 px-8 text-base" data-testid="hero-join-btn">
                    Become a Member <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
                <Button asChild variant="outline" className="rounded-none border-foreground h-12 px-8 text-base font-bold" data-testid="hero-explore-btn">
                  <Link to="/map">Explore the Map</Link>
                </Button>
              </div>
            </motion.div>
          </div>
          <div className="md:col-span-4 flex flex-col justify-end gap-4">
            {[["1,200+", "Members"], ["3", "NFPA Standards"], ["Daily", "Code Digest"]].map(([n, l], i) => (
              <motion.div key={l} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }}
                className="border border-border bg-white p-6 flex items-baseline justify-between">
                <span className="font-display text-4xl font-black">{n}</span>
                <span className="label-tech text-muted-foreground">{l}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STANDARDS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <span className="label-tech text-primary">Codes Covered</span>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-bold mt-2">The three standards we live by.</h2>
          </div>
          <Button asChild variant="link" className="text-primary font-bold">
            <Link to="/knowledge">Open knowledge base <ArrowRight className="w-4 h-4 ml-1" /></Link>
          </Button>
        </div>
        <div className="grid md:grid-cols-3 gap-0 border border-border">
          {standards.map((s, i) => (
            <Link key={s.code} to="/knowledge" className="group p-8 border-border md:border-r last:border-r-0 border-b md:border-b-0 hover:bg-[#0A0A0A] transition-colors" data-testid={`standard-${s.code.replace(" ", "-")}`}>
              <s.icon className="w-8 h-8 text-primary mb-6" />
              <span className="font-mono text-sm text-primary">{s.code}</span>
              <h3 className="font-display text-2xl font-bold mt-1 group-hover:text-white transition-colors">{s.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground group-hover:text-gray-400 transition-colors leading-relaxed">{s.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* AD BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <AdSlot placement="banner" />
      </section>

      {/* FEATURES */}
      <section className="bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <Link to={f.to} className="tech-card block p-8 h-full" data-testid={`feature-${f.title.toLowerCase().replace(/\s/g, "-")}`}>
                <f.icon className="w-8 h-8 text-primary mb-6" />
                <h3 className="font-display text-xl font-bold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                <span className="inline-flex items-center gap-1 mt-4 text-sm font-bold text-primary">Open <ArrowRight className="w-4 h-4" /></span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* COMMUNITY / FORUM PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <img src={COMMUNITY} alt="Engineers on site" className="w-full h-80 object-cover border border-border" />
          <div className="absolute -bottom-4 -right-4 bg-primary text-white p-4 hidden sm:block">
            <span className="font-display font-black text-2xl">Live</span>
            <span className="block label-tech">Discussion</span>
          </div>
        </div>
        <div>
          <span className="label-tech text-primary">Community</span>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-bold mt-2">Recent discussions from the field.</h2>
          <div className="mt-6 divide-y divide-border border-t border-b border-border">
            {threads.slice(0, 4).map((t) => (
              <Link key={t.id} to={`/forum/${t.id}`} className="flex items-center justify-between py-4 group" data-testid={`home-thread-${t.id}`}>
                <div>
                  <p className="font-semibold group-hover:text-primary transition-colors">{t.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t.author_name} · {t.reply_count} replies</p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
              </Link>
            ))}
            {threads.length === 0 && <p className="py-4 text-sm text-muted-foreground">No discussions yet — start one!</p>}
          </div>
          <Button asChild className="rounded-none bg-primary hover:bg-[#D62828] font-bold mt-6">
            <Link to="/forum">Visit the Forum</Link>
          </Button>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center">
          <h2 className="font-display text-4xl lg:text-5xl tracking-tighter font-black">Join the network today.</h2>
          <p className="mt-4 text-gray-400 max-w-xl mx-auto">Membership is free. Get on the map, join the conversation, and receive a daily NFPA code snippet.</p>
          {!user && (
            <Button onClick={openAuth} className="rounded-none bg-primary hover:bg-[#D62828] font-bold h-12 px-10 mt-8 text-base" data-testid="cta-join-btn">
              Create Your Membership
            </Button>
          )}
        </div>
      </section>
    </div>
  );
}
